import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TeamProvider } from './context/TeamContext';
import { ThemeProvider } from './context/ThemeContext';
import MainLayout from './components/layout/MainLayout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Reports from './pages/Reports';
import Groups from './pages/Groups';
import Emails from './pages/Emails';
import Users from './pages/Users';
import SftpSites from './pages/SftpSites';
import PickupFolders from './pages/PickupFolders';
import Settings from './pages/Settings';
import DeliveryAudit from './pages/DeliveryAudit';
import MyReports from './pages/MyReports';
import TeamList from './pages/Teams/TeamList';
import TeamCreate from './pages/Teams/TeamCreate';
import TeamDetail from './pages/Teams/TeamDetail';
import ManagerAssignment from './pages/ManagerAssignment';
import './App.css';

/**
 * Protected Route Component
 * Redirects to login if user is not authenticated.
 * Shows "not authorized" message if authenticated but not authorized (SSO user not pre-created).
 */
function ProtectedRoute({ children }) {
  const { isAuthenticated, isAuthorized, loading, error, logout } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAuthorized) {
    return (
      <div className="loading-screen">
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '2rem' }}>
          <h2 style={{ marginBottom: '1rem', color: 'var(--color-text-primary, #fff)' }}>Access Not Authorized</h2>
          <p style={{ marginBottom: '1.5rem', color: 'var(--color-text-secondary, #aaa)' }}>
            {error || 'Your account has not been set up yet. Please contact your administrator to be added to TUI ReportHub.'}
          </p>
          <button
            onClick={() => logout()}
            style={{
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              background: 'var(--color-primary, #3b82f6)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return children;
}

/**
 * Public Route Component
 * Redirects to dashboard if user is already authenticated,
 * unless they just signed out (prevents redirect loop with SSO).
 */
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const searchParams = new URLSearchParams(window.location.search);
  const justSignedOut = searchParams.get('signed-out') === 'true'
    || sessionStorage.getItem('justSignedOut') === 'true';

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (isAuthenticated && !justSignedOut) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/**
 * Tab configuration for main navigation
 */
const tabs = [
  {
    path: '/my-reports',
    label: 'My Reports',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
    viewerVisible: true
  },
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
      </svg>
    )
  },
  {
    path: '/reports',
    label: 'Reports',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    )
  },
  {
    path: '/groups',
    label: 'Groups',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    )
  },
  {
    path: '/emails',
    label: 'Emails',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    )
  },
  {
    path: '/delivery-audit',
    label: 'Delivery Audit',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    )
  },
  {
    path: '/admin',
    label: 'Admin',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    adminOnly: true,
    managerVisible: true,
    children: [
      { path: '/admin/users', label: 'Users', adminOnly: true },
      { path: '/admin/teams', label: 'Teams', adminOnly: true },
      { path: '/admin/team-assignment', label: 'Team Assignment', adminOnly: true },
      { path: '/admin/sftp-sites', label: 'SFTP Sites', adminOnly: true },
      { path: '/admin/pickup-folders', label: 'Pickup Folders' },
      { path: '/admin/categories', label: 'Categories' },
    ]
  }
];
/**
 * Role-gated route wrapper.
 * Redirects viewers to /my-reports if they try to access manager/admin pages.
 */
function RequireRole({ minRole, children }) {
  const { user } = useAuth();
  const hierarchy = { admin: 3, manager: 2, viewer: 1 };
  const userLevel = hierarchy[user?.role] || 0;
  const requiredLevel = hierarchy[minRole] || 0;
  if (userLevel < requiredLevel) {
    return <Navigate to="/my-reports" replace />;
  }
  return children;
}

/**
 * Smart default route — viewers go to My Reports, everyone else to Dashboard.
 */
function DefaultRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.role === 'viewer' ? '/my-reports' : '/dashboard'} replace />;
}

/**
 * Main App Component
 */
function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
        <Routes>
          {/* Public Routes - No Layout */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          {/* Protected Routes - With MainLayout */}
          <Route
            element={
              <ProtectedRoute>
                <TeamProvider>
                  <MainLayout showTabs={true} tabs={tabs} />
                </TeamProvider>
              </ProtectedRoute>
            }
          >
            <Route path="/dashboard" element={<RequireRole minRole="manager"><Dashboard /></RequireRole>} />
            <Route path="/my-reports" element={<MyReports />} />
            <Route path="/reports" element={<RequireRole minRole="manager"><Reports /></RequireRole>} />
            <Route path="/groups" element={<RequireRole minRole="manager"><Groups /></RequireRole>} />
            <Route path="/emails" element={<RequireRole minRole="manager"><Emails /></RequireRole>} />
            <Route path="/delivery-audit" element={<RequireRole minRole="manager"><DeliveryAudit /></RequireRole>} />
            <Route path="/admin/users" element={<RequireRole minRole="admin"><Users /></RequireRole>} />
            <Route path="/admin/teams" element={<RequireRole minRole="admin"><TeamList /></RequireRole>} />
            <Route path="/admin/teams/create" element={<RequireRole minRole="admin"><TeamCreate /></RequireRole>} />
            <Route path="/admin/teams/:teamId" element={<RequireRole minRole="admin"><TeamDetail /></RequireRole>} />
            <Route path="/admin/team-assignment" element={<RequireRole minRole="admin"><ManagerAssignment /></RequireRole>} />
            <Route path="/admin/sftp-sites" element={<RequireRole minRole="admin"><SftpSites /></RequireRole>} />
            <Route path="/admin/pickup-folders" element={<RequireRole minRole="manager"><PickupFolders /></RequireRole>} />
            <Route path="/admin/categories" element={<RequireRole minRole="manager"><Settings /></RequireRole>} />
          </Route>

          {/* Default Route */}
          <Route path="/" element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />

          {/* 404 Route */}
          <Route path="*" element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
        </Routes>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
